import React, { useEffect, useState } from "react";
import { Plus, Tv, Trash2, Loader2, RadioTower, Key } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Per-user IPTV source manager. Lives on the Settings page so the user
 * can paste M3U / XMLTV URLs (or Xtream Codes credentials) with a real
 * keyboard. Once added, sources surface inside the APK's Live TV browser
 * under a synthetic "My IPTV" category — no client changes needed.
 */
export default function IptvSourcesSection() {
  const [sources, setSources] = useState(null);  // null = loading
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState("m3u");      // "m3u" | "xtream"
  const [form, setForm] = useState({
    name: "",
    m3u_url: "",
    epg_url: "",
    xtream_server: "",
    xtream_username: "",
    xtream_password: "",
  });
  const [deletingId, setDeletingId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/iptv/sources");
      setSources(Array.isArray(data) ? data : []);
    } catch {
      // Silent on initial load — guests (401) shouldn't see a scary toast.
      setSources([]);
    }
  };

  useEffect(() => { reload(); }, []);

  const add = async (e) => {
    e?.preventDefault();
    if (mode === "m3u" && !form.m3u_url.trim()) {
      toast.error("M3U URL is required");
      return;
    }
    if (mode === "xtream" && (!form.xtream_server.trim() || !form.xtream_username.trim() || !form.xtream_password.trim())) {
      toast.error("Xtream server URL, username and password are all required");
      return;
    }
    setAdding(true);
    try {
      const body = { name: form.name.trim() || "My IPTV" };
      if (mode === "m3u") {
        body.m3u_url = form.m3u_url.trim();
        body.epg_url = form.epg_url.trim() || null;
      } else {
        body.xtream_server   = form.xtream_server.trim();
        body.xtream_username = form.xtream_username.trim();
        body.xtream_password = form.xtream_password;
      }
      await api.post("/iptv/sources", body);
      setForm({
        name: "", m3u_url: "", epg_url: "",
        xtream_server: "", xtream_username: "", xtream_password: "",
      });
      toast.success("Source added — it'll show up under 'My IPTV' on your Fire TV.");
      reload();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to add source");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (sourceId) => {
    setDeletingId(sourceId);
    try {
      await api.delete(`/iptv/sources/${sourceId}`);
      toast.success("Source removed");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    } finally {
      setDeletingId(null);
    }
  };

  const tabBtn = (id, label, Icon) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      data-testid={`iptv-mode-${id}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors ${
        mode === id
          ? "bg-pink-500 text-white shadow-[0_0_10px_-2px_rgba(255,42,109,0.55)]"
          : "bg-[var(--tt-surface)] border border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-pink-400/50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  return (
    <section
      className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5"
      data-testid="settings-iptv-card"
    >
      <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
        <RadioTower className="w-5 h-5 text-pink-400" /> My IPTV sources
      </h2>
      <p className="text-xs text-[var(--tt-text-muted)] mb-4">
        Add your own M3U playlists OR Xtream Codes provider login. They&apos;ll appear under
        <span className="text-pink-300 font-semibold"> &quot;My IPTV&quot;</span> on your Fire TV — only you can see them.
      </p>

      {/* Existing sources */}
      {sources === null ? (
        <div className="flex items-center gap-2 text-xs text-[var(--tt-text-muted)] py-3" data-testid="iptv-sources-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : sources.length > 0 ? (
        <ul className="mb-4 space-y-2" data-testid="iptv-sources-list">
          {sources.map((s) => (
            <li
              key={s.source_id}
              className="flex items-start gap-3 p-3 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)]"
              data-testid={`iptv-source-${s.source_id}`}
            >
              {s.kind === "xtream"
                ? <Key className="w-4 h-4 flex-none mt-0.5 text-amber-400" />
                : <Tv className="w-4 h-4 flex-none mt-0.5 text-pink-400" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-2">
                  {s.name || "My IPTV"}
                  {s.kind === "xtream" && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30">
                      Xtream
                    </span>
                  )}
                </div>
                {s.kind === "xtream" && s.xtream?.server ? (
                  <div className="text-[11px] text-[var(--tt-text-muted)] truncate">
                    {s.xtream.server} · <span className="text-amber-300">{s.xtream.username}</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-[var(--tt-text-muted)] truncate" title={s.m3u_url}>
                    {s.m3u_url}
                  </div>
                )}
                {s.epg_url && s.kind !== "xtream" && (
                  <div className="text-[10px] text-[var(--tt-text-faint)] truncate" title={s.epg_url}>
                    Guide: {s.epg_url}
                  </div>
                )}
                {s.channel_count != null && (
                  <div className="text-[10px] text-emerald-400 mt-1">
                    {s.channel_count} channels cached
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(s.source_id)}
                disabled={deletingId === s.source_id}
                data-testid={`iptv-source-${s.source_id}-delete`}
                className="p-2 rounded-md hover:bg-red-500/10 text-[var(--tt-text-muted)] hover:text-red-400 transition-colors disabled:opacity-50"
                title="Remove this source"
              >
                {deletingId === s.source_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mb-4 text-xs text-[var(--tt-text-muted)]" data-testid="iptv-sources-empty">
          No sources yet — add one below.
        </div>
      )}

      {/* Add form */}
      <form
        onSubmit={add}
        className="space-y-2 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg p-4"
        data-testid="iptv-add-form"
      >
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tt-text-muted)]">
            Add a source
          </div>
          <div className="flex items-center gap-1">
            {tabBtn("m3u",    "M3U URL",      Tv)}
            {tabBtn("xtream", "Xtream Codes", Key)}
          </div>
        </div>

        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Name (e.g. UK Bundle)"
          maxLength={60}
          data-testid="iptv-add-name"
          className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
        />

        {mode === "m3u" ? (
          <>
            <input
              type="url"
              value={form.m3u_url}
              onChange={(e) => setForm({ ...form, m3u_url: e.target.value })}
              placeholder="M3U URL  (https://…/playlist.m3u8)"
              required
              data-testid="iptv-add-m3u"
              className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
            />
            <input
              type="url"
              value={form.epg_url}
              onChange={(e) => setForm({ ...form, epg_url: e.target.value })}
              placeholder="EPG XMLTV URL — optional (https://…/guide.xml or .gz)"
              data-testid="iptv-add-epg"
              className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
            />
          </>
        ) : (
          <>
            <input
              type="url"
              value={form.xtream_server}
              onChange={(e) => setForm({ ...form, xtream_server: e.target.value })}
              placeholder="Server URL  (http://provider.tv:8080)"
              required
              data-testid="iptv-add-xtream-server"
              className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={form.xtream_username}
                onChange={(e) => setForm({ ...form, xtream_username: e.target.value })}
                placeholder="Username"
                required
                autoComplete="username"
                data-testid="iptv-add-xtream-username"
                className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
              />
              <input
                type="password"
                value={form.xtream_password}
                onChange={(e) => setForm({ ...form, xtream_password: e.target.value })}
                placeholder="Password"
                required
                autoComplete="new-password"
                data-testid="iptv-add-xtream-password"
                className="w-full text-sm bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-md px-3 py-2 focus:outline-none focus:border-pink-400"
              />
            </div>
            <div className="text-[10px] text-[var(--tt-text-faint)] leading-snug">
              We&apos;ll auto-derive the M3U &amp; XMLTV URLs from your Xtream credentials so you don&apos;t have to.
              Channels appear under <span className="text-pink-300">&quot;My IPTV&quot;</span> on the Fire TV.
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={adding}
          data-testid="iptv-add-submit"
          className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-400 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md transition-colors"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add source
        </button>
      </form>
    </section>
  );
}
